import { useCallback, useEffect, useRef } from 'react'
import type { JSX } from 'react'

import {
  Dialog,
  Drawer,
  Flex,
  Heading,
  Paragraph,
  toast,
  useIsMobileScreenSize,
  VisuallyHidden,
  WarningIcon,
} from '@buffer-mono/popcorn'

import getErrorBoundary from '~publish/components/ErrorBoundary'

import type { CommandBarAction } from './actions/types'
import { CommandBarContent } from './CommandBarContent'
import { CommandBarProvider } from './context/CommandBarContext'
import { useActions } from './hooks/useActions'
import { useCommandBar } from './hooks/useCommandBar'
import { useCommandBarTracking } from './hooks/useCommandBarTracking'
import { useNavigationShortcuts } from './hooks/useNavigationShortcuts'

import styles from './CommandBar.module.css'

const ErrorBoundary = getErrorBoundary()

function CommandBarErrorFallback(): JSX.Element {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="md"
      className={styles.errorFallback}
    >
      <WarningIcon size="large" color="critical" />
      <Heading size="small" as="h3">
        Something went wrong
      </Heading>
      <Paragraph color="subtle" align="center">
        The command bar ran into an issue. Try closing and reopening it.
      </Paragraph>
    </Flex>
  )
}

function CommandBarWrapper({
  open,
  onOpenChange,
  onCloseAutoFocus,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCloseAutoFocus: (event: Event) => void
  children: React.ReactNode
}): JSX.Element {
  const isMobile = useIsMobileScreenSize()

  return isMobile ? (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <Drawer.Content
        className={styles.drawerContent}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <VisuallyHidden>
          <Drawer.Title>Command Bar</Drawer.Title>
          <Drawer.Description>
            Search and navigate to any action in Buffer
          </Drawer.Description>
        </VisuallyHidden>
        {children}
      </Drawer.Content>
    </Drawer>
  ) : (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        size="large"
        className={styles.dialogContent}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <VisuallyHidden>
          <Dialog.Title>Command Bar</Dialog.Title>
          <Dialog.Description>
            Search and navigate to any action in Buffer
          </Dialog.Description>
        </VisuallyHidden>
        {children}
      </Dialog.Content>
    </Dialog>
  )
}

export function CommandBar(): JSX.Element {
  const { open, openSource, setOpen, closeCommandBar, restoreFocusOnClose } =
    useCommandBar()
  const { trackOpen, trackActionSelected, trackSavedViewLoaded } =
    useCommandBarTracking()
  const { actions, loading, executeAction, frecencyData, setSearchQuery } =
    useActions({ onSavedViewSelected: trackSavedViewLoaded })
  useNavigationShortcuts()

  const wasOpenRef = useRef(false)

  // Track when command bar opens
  useEffect(() => {
    if (open && !wasOpenRef.current && openSource) {
      trackOpen(openSource)
    }
    wasOpenRef.current = open
  }, [open, openSource, trackOpen])

  const handleSelectAction = useCallback(
    (action: CommandBarAction, searchQuery: string) => {
      const position = actions.findIndex((a) => a.id === action.id)
      trackActionSelected(action, searchQuery, position)
      closeCommandBar()
      executeAction(action).catch(() => {
        toast.error('Something went wrong. Please try again.')
      })
    },
    [actions, trackActionSelected, closeCommandBar, executeAction],
  )

  return (
    <CommandBarWrapper
      open={open}
      onOpenChange={setOpen}
      onCloseAutoFocus={restoreFocusOnClose}
    >
      <ErrorBoundary fallbackComponent={CommandBarErrorFallback}>
        <CommandBarContent
          actions={actions}
          loading={loading}
          onSelectAction={handleSelectAction}
          onQueryChange={setSearchQuery}
          frecencyData={frecencyData}
        />
      </ErrorBoundary>
    </CommandBarWrapper>
  )
}

CommandBar.Provider = CommandBarProvider
